import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingContactIcons from "@/components/FloatingContactIcons";
import { Building, Home, Users, Shield, Settings, Handshake } from "lucide-react";

const ServicesPage = () => {
  const services = [
    {
      icon: Building,
      title: "Property Acquisition",
      description: "Your partner in finding the perfect property.",
      details: "We simplify the search for your dream home or next investment. Moving beyond online listings, we leverage our local expertise to identify off-market opportunities and properties that match your specific criteria. From initial viewing to offer negotiation, we guide you through every step to secure the best possible value in Nairobi's competitive market."
    },
    {
      icon: Home,
      title: "Developer Marketing & Sales",
      description: "Bringing visionary developments to the market.",
      details: "We act as the bridge between developers and qualified buyers. Our team creates bespoke marketing strategies to showcase new developments, handling everything from branding and staging to buyer qualification and sales. We ensure your project achieves maximum visibility and rapid turnover."
    },
    {
      icon: Users,
      title: "Rental Services (Furnished & Unfurnished)",
      description: "Flexible living solutions for every lifestyle.",
      details: "We manage the full rental cycle for both landlords and tenants. Whether you are seeking a premium furnished executive suite for a short-term stay or a long-term unfurnished family home, we maintain a vetted portfolio of quality properties. For landlords, we ensure minimal vacancy periods and reliable, pre-qualified tenants."
    },
    {
      icon: Shield,
      title: "Legal Coordination",
      description: "Seamless transactions with dedicated legal oversight.",
      details: "We believe due diligence is non-negotiable. We work exclusively with a dedicated team of property lawyers who specialize in conveyancing and title verification. This integrated legal support ensures that every contract, transfer, and search is handled with precision, protecting your interests and providing complete peace of mind."
    },
    {
      icon: Settings,
      title: "Comprehensive Property Management",
      description: "Protecting your asset, enhancing your return.",
      details: "Maximize the value of your investment without the daily hassle. We act as your personal estate manager, handling everything from tenant sourcing and rent collection to routine maintenance and emergency repairs. Our proactive approach preserves the condition of your property while ensuring steady, reliable income."
    },
    {
      icon: Handshake,
      title: "Client Representation",
      description: "Your interests, professionally represented.",
      details: "Navigate the complex real estate landscape with a trusted advisor by your side. We offer exclusive buyer and seller representation, ensuring your interests are protected during negotiations and transactions. Whether navigating regulatory requirements or negotiating complex deals, we provide the strategic advice you need to make informed decisions."
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-32 md:pt-36 w-full">
        {/* Hero Section */}
        <div className="bg-brand-gradient py-20 md:py-32">
          <div className="px-6 md:px-16 text-center">
            <h1 className="font-open-sans font-bold text-4xl md:text-6xl uppercase tracking-[0.15em] text-metallic-tan mb-4">
              Our Services
            </h1>
            <div className="font-open-sans font-semibold text-xl md:text-2xl uppercase tracking-[0.1em] text-metallic-tan mb-6">
              Comprehensive Real Estate Solutions
            </div>
            <div className="relative my-6">
              <div className="h-px w-40 md:w-48 bg-white mx-auto"></div>
            </div>
            <p className="font-open-sans font-semibold italic text-lg md:text-xl lowercase text-dusty-rose max-w-3xl mx-auto">
              tailored excellence for every real estate need
            </p>
          </div>
        </div>

        {/* Services Grid */}
        <div className="px-6 md:px-16 py-16 md:py-24">
          <div className="max-w-7xl mx-auto">
            <div className="grid gap-8 md:gap-12">
              {services.map((service, index) => (
                <div key={service.title} className="bg-brand-surface border border-border/20 rounded-2xl p-8 md:p-10 backdrop-blur-sm">
                  <div className="flex flex-col lg:flex-row gap-8">
                    {/* Icon and Title */}
                    <div className="lg:w-1/3">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-metallic-tan/20 rounded-lg flex items-center justify-center">
                          <service.icon className="w-6 h-6 text-metallic-tan" />
                        </div>
                        <h3 className="font-open-sans font-bold text-xl md:text-2xl text-metallic-tan">
                          {service.title}
                        </h3>
                      </div>
                      <p className="font-open-sans font-semibold text-dusty-rose italic text-lg mb-6">
                        {service.description}
                      </p>
                    </div>

                    {/* Detailed Description */}
                    <div className="lg:w-2/3">
                      <div className="bg-brand-canvas rounded-xl p-6 border border-metallic-tan/20">
                        <p className="font-open-sans text-base md:text-lg leading-relaxed text-white">
                          {service.details}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-brand-gradient py-16 md:py-24">
          <div className="px-6 md:px-16 text-center">
            <h2 className="font-open-sans font-bold text-3xl md:text-4xl uppercase tracking-[0.15em] text-metallic-tan mb-6">
              Ready to Get Started?
            </h2>
            <p className="font-open-sans font-semibold text-lg text-white mb-8 max-w-2xl mx-auto">
              Contact us today to discuss how our comprehensive services can help you achieve your real estate goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="inline-block bg-metallic-tan text-emerald-green font-open-sans font-bold px-8 py-4 rounded-lg tracking-[0.1em] uppercase hover:bg-metallic-tan/90 transition-colors">
                Schedule Consultation
              </a>
              <a href="tel:+254700000000" className="inline-block border border-metallic-tan text-metallic-tan font-open-sans font-bold px-8 py-4 rounded-lg tracking-[0.1em] uppercase hover:bg-metallic-tan/10 transition-colors">
                Call Us
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <FloatingContactIcons />
    </div>
  );
};

export default ServicesPage;
